import { t } from 'ttag';
import { useEffect, useMemo, useRef } from 'react';
import { matchPath, Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import cookie from 'js-cookie';
import { Helmet } from 'react-helmet-async';
import queryString from 'query-string';
import isEqual from 'lodash/isEqual';

import { Catalog } from '@components/catalog/Catalog';
import { ProductPaginationType } from '@models';
import { ADULT_USER_COOKIE } from '@constants';
import {
  convertFiltersToSearchParams,
  useFilterData,
  useIndexedFilters,
} from '@hook/productFilters';
import { edgesToProducts } from '@utils/products';
import { getShafaAbsoluteUrl, useIsClient, useSearchParams } from '@utils/common';
import useUpdateEffect from '@hook/useUpdateEffect';
import { AgeConfirmationModal } from '@components/AgeConfirmationModal';
import { StructuredData } from '@containers/catalog/StructuredData';
import { baseCatalogIndexedFilter, catalogIndexedFilter } from '@urls';
import { sendCatalogIntentEvent, sendRTBHouseCategoryEvent } from '@utils/analytics';
import { useSeoHubData } from '@containers/catalog/Catalog/hooks';
import { PageMetaTitle } from '@components/PageMetaTitle';
import { PageMetaDescription } from '@components/PageMetaDescription';

import { useCatalogInfoQuery } from './gql/graphql.v5.generated';
import {
  CatalogCityInfoQuery,
  useCatalogCityInfoQuery,
  useCatalogProductsQuery,
  useCatalogTopProductsV2Query,
} from './gql/graphql.v3.generated';

type Props = {
  namespace: string
  slug?: string
  citySlug?: string
  className?: string
};

const PRODUCTS_PER_PAGE = 44;
const CITY_SLUG_COUNT_FOR_NO_INDEX = 100;

export const CatalogContainer: React.FC<Props> = ({
  namespace,
  slug,
  citySlug,
  className,
}) => {
  const location = useLocation();
  const isClient = useIsClient();
  const history = useHistory();
  const [params] = useSearchParams();
  const { filters, hasFilters } = useFilterData();
  const {
    basePath: baseIndexedFilterPath,
    activeFilters: activeIndexedFilters,
    hasFilters: hasIndexedFilters,
    indexedFiltersInput,
    indexedFiltersInputLegacy,
  } = useIndexedFilters();
  const indexedFilterMatch = matchPath(
    location.pathname,
    [
      baseCatalogIndexedFilter,
      catalogIndexedFilter,
    ],
  );
  const citiesDataRef = useRef<CatalogCityInfoQuery>(undefined);

  const {
    data: cityQueryData,
    loading: cityLoading,
  } = useCatalogCityInfoQuery({
    variables: {
      citySlug: citySlug!,
    },
    skip: !citySlug,
  });

  const cityData: CatalogCityInfoQuery | undefined = useMemo(
    () => {
      if (cityLoading) {
        return citiesDataRef.current;
      }
      citiesDataRef.current = cityQueryData;
      return cityQueryData;
    },
    [cityQueryData, cityLoading],
  );

  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
  } = useCatalogProductsQuery({
    variables: {
      ...filters,
      cities: cityData?.city ? [cityData.city.id] : filters.cities,
      ukrainian: !!filters.ukrainian,
      priceId: filters.prices,
      catalogSlug: slug || namespace,
      lastViewedProductId: params.last_viewed_product_id
        ? (+params.last_viewed_product_id || undefined)
        : undefined,
      indexedFilter: indexedFiltersInputLegacy,
      pageNum: +(params.page || 1),
      first: PRODUCTS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: cityLoading && !cityData && !isClient,
  });
  const {
    data: topProductsData,
    loading: topProductsLoading,
    refetch: refetchTopProducts,
  } = useCatalogTopProductsV2Query({
    variables: {
      ...filters,
      ukrainian: !!filters.ukrainian,
      priceId: filters.prices,
      catalogSlug: slug || namespace,
      first: 8,
      pageNum: +(params.page || 1),
      paginationType: ProductPaginationType.Page,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !!citySlug,
  });

  const { data: catalogData, error: catalogError } = useCatalogInfoQuery({
    variables: {
      citySlug,
      slug: slug || namespace,
      indexedFilters: indexedFiltersInput,
    },
    context: {
      v5: true,
    },
    fetchPolicy: 'network-only',
  });

  const {
    filterThematics,
    seoHubItems,
    loaded: seoHubLoaded,
  } = useSeoHubData();

  useUpdateEffect(() => {
    refetchTopProducts();
  }, [params.page, refetchTopProducts]);

  useEffect(() => {
    sendRTBHouseCategoryEvent(slug || namespace);
  }, [namespace, slug]);

  useEffect(() => {
    if (
      catalogData?.catalog
      && !catalogData.catalog.children.length
      && !hasFilters
    ) {
      sendCatalogIntentEvent();
    }
  }, [catalogData?.catalog, hasFilters]);

  const products = useMemo(
    () => edgesToProducts(
      productsData?.products?.edges || [],
    ),

    [productsData?.products?.edges],
  );
  const mostFrequentCatalogSlug = productsData?.products?.mostFrequentCatalogSlug;

  const topProducts = useMemo(
    () => {
      if (topProductsData) {
        return edgesToProducts(topProductsData?.topProductsV2.edges || []);
      }
      return [];
    },
    [topProductsData],
  );

  const title = catalogData?.catalog?.seoTitle || t`Каталог`;
  const city = cityData?.city;
  const indexedFilter = catalogData?.catalog.indexedFilter;
  const productsCount = productsData?.products?.pageInfo.total || 0;
  const totalPages = (
    Math.floor(productsCount / PRODUCTS_PER_PAGE)
    + (productsCount % PRODUCTS_PER_PAGE ? 1 : 0)
  );

  let meta;
  if (citySlug && productsCount < CITY_SLUG_COUNT_FOR_NO_INDEX) {
    meta = (
      <meta name="robots" content="noindex, nofollow" />
    );
  } else if (productsCount === 0) {
    meta = (
      <meta name="robots" content="noindex, nofollow" />
    );
  } else if (
    !(Object.keys(params).length === 1 && !!filters.searchText) && (
      Object.keys(params).length
      && (Object.keys(params).length !== 1 || !!params.page)
    )
  ) {
    meta = (
      <meta name="robots" content="noindex, follow" />
    );
  } else {
    if (indexedFilter?.canonicalTo) {
      meta = (
        <link
          rel="canonical"
          href={getShafaAbsoluteUrl(history.createHref({ pathname: indexedFilter?.canonicalTo }))}
        />
      );
    } else {
      meta = (
        <link
          rel="canonical"
          href={getShafaAbsoluteUrl(history.createHref({ pathname: history.location.pathname }))}
        />
      );
    }
  }

  const hasNotFoundError = catalogError?.graphQLErrors.find(
    ({ path, extensions }) => (
      extensions?.code === 'NOT_FOUND'
      && (
        isEqual(path, ['catalog', 'seoTitle'])
        || isEqual(path, ['catalog', 'seoDescription'])
        || isEqual(path, ['catalog', 'metaTitle'])
        || isEqual(path, ['catalog', 'metaDescription'])
      )
    ),
  );

  // If catalog with non existing indexed filters requested
  // Redirect to the same page with non indexed filters
  if (
    (hasIndexedFilters && hasNotFoundError)
    || (indexedFilterMatch && !Object.keys(activeIndexedFilters).length)
  ) {
    return (
      <Redirect
        to={{
          pathname: `/${baseIndexedFilterPath}`,
          search: queryString.stringify(convertFiltersToSearchParams(filters)),
        }}
      />
    );
  }

  if (indexedFilter?.redirectTo) {
    return (
      <Redirect
        to={{ pathname: indexedFilter?.redirectTo }}
      />
    );
  }

  return (
    <>
      {/* TODO(a.kravchuk): Fix it once we have 404 page */}
      {
        productsError?.message === 'Catalog matching query does not exist.' && (
          <Route
            render={({ staticContext }) => {
              if (staticContext) {
                staticContext.statusCode = 404;
              }
              return null;
            }}
          />
        )
      }

      {
        catalogData?.catalog && (
          <>
            <PageMetaTitle title={catalogData.catalog.metaTitle} />
            {
              catalogData.catalog.metaDescription && (
                <PageMetaDescription description={catalogData.catalog.metaDescription} />
              )
            }
          </>
        )
      }

      {
        !productsLoading && catalogData?.catalog && (
          <>
            <Helmet titleTemplate="%s">
              {meta}
            </Helmet>
            <StructuredData
              metaTitle={catalogData.catalog.metaTitle}
              metaDescription={catalogData.catalog.metaDescription}
              productsCount={productsCount}
              products={products}
            />
          </>
        )
      }

      <Catalog
        className={className}
        productsLoading={productsLoading || topProductsLoading}
        catalogSlug={slug || namespace}
        mostFrequentCatalogSlug={mostFrequentCatalogSlug}
        breadcrumbs={
          catalogData?.catalog?.breadcrumbs.map(
            (catalog) => ({
              title: catalog.name,
              url: catalog.url + (city ? `/gorod-${city.slug}` : ''),
            }),
          )
        }
        title={title}
        productsCount={productsCount}
        totalPages={totalPages}
        products={products}
        topProducts={topProducts}
        topLevelCategories={productsData?.products?.topLevelCategories || []}
        seoHubData={{
          filterThematics,
          seoHubItems,
        }}
        seoHubLoaded={seoHubLoaded}
      />

      <AgeConfirmationModal
        isOpen={isClient && !!catalogData?.catalog?.isAdultOnly && !cookie.get(ADULT_USER_COOKIE)}
        onCancel={() => {
          window.location.href = '/';
        }}
        onConfirm={() => {
          cookie.set(ADULT_USER_COOKIE, 'true');
          window.location.reload();
        }}
      />
    </>
  );
};
